import React, { useState } from 'react';
import { Typography, Box, Stack, Card, CardContent, CardActionArea, useTheme, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import SportsGymnasticsIcon from '@mui/icons-material/SportsGymnastics';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import { useSession } from '@platform/core/src/context/SessionContext';
import { useUI } from '@platform/core/src/context/UIContext';
import { useResources } from '@platform/core/src/context/ResourceContext';
import { DomainObjectModal } from '@platform/core/src/components/domain';
import { startSessionButtons } from '../../config/app/sessionConfig';
import { workoutPlanConfig, bioLogConfig } from '../../config/domainConfig';

const getConfig = (id) => startSessionButtons.find(b => b.id === id);

const OptionCard = ({ title, subtitle, icon: Icon, color, onClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      elevation={0}
      sx={{
        flex: '1 1 280px',
        minWidth: 0,
        borderRadius: 2,
        overflow: 'visible',
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(135deg, ${alpha(color, 0.15)} 0%, ${alpha(theme.palette.background.paper, 0.4)} 100%)`
          : `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, #ffffff 100%)`,
        border: `1px solid ${alpha(color, 0.3)}`,
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 12px ${alpha(color, 0.15)}`,
          borderColor: color,
          '& .action-arrow': {
            transform: 'translateX(2px)',
            opacity: 1
          }
        }
      }}
    >
      <CardActionArea onClick={onClick} sx={{ borderRadius: 2, height: '100%' }}>
        <CardContent sx={{
            py: isMobile ? 1.5 : 2,
            px: isMobile ? 1.5 : 2.5,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            '&:last-child': { pb: isMobile ? 1.5 : 2 }
        }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
            <Box sx={{
              width: isMobile ? 36 : 44,
              height: isMobile ? 36 : 44,
              borderRadius: '10px',
              bgcolor: color,
              color: theme.palette.getContrastText(color),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 2px 6px ${alpha(color, 0.4)}`,
              flexShrink: 0
            }}>
              <Icon fontSize="small" />
            </Box>
            <Box sx={{ flexGrow: 1, textAlign: 'left', minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight="bold" color="text.primary" sx={{ lineHeight: 1.2, fontSize: '0.95rem', mb: 0.25 }}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.3 }}>
                {subtitle}
              </Typography>
            </Box>
            <ChevronRightIcon
                className="action-arrow"
                sx={{
                    color: color,
                    fontSize: 20,
                    opacity: 0.6,
                    transition: 'all 0.2s ease',
                    flexShrink: 0
                }}
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const CoachWelcomeOptions = () => {
  const { initializeSession, teamName } = useSession();
  const { closeAllResourceTabs, openResourceTab } = useUI();
  const { upsertResource } = useResources();
  const theme = useTheme();
  
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isBioLogModalOpen, setIsBioLogModalOpen] = useState(false);

  const isDefaultTeam = !teamName || teamName === 'welcome_group';

  if (!isDefaultTeam) {
    return null;
  }

  const handleDesignWithCoachClick = () => {
    const config = getConfig('design-workout');
    closeAllResourceTabs();
    initializeSession(null, {
        team_name: config?.teamName || 'interactive_workout_plan_team',
    });
  };

  const handleManualPlanClick = () => {
    setIsPlanModalOpen(true);
  };

  const handleLogClick = () => {
    const config = getConfig('workout-session');
    closeAllResourceTabs();
    initializeSession(null, {
        team_name: config?.teamName || 'active_workout_team',
        preChatConfig: config?.preChatConfig,
        clearWorkspace: true
    });
  };

  const handleAddBioLogClick = () => {
    setIsBioLogModalOpen(true);
  };

  const handleCreateSuccess = (action, newItem) => {
    setIsPlanModalOpen(false);
    if ((action === 'create' || action === 'create_and_view') && newItem) {
        upsertResource(newItem);
        openResourceTab({ ...newItem, type: 'workout_plan' });
    }
  };

  const handleBioLogCreateSuccess = (action, newItem) => {
    setIsBioLogModalOpen(false);
    if ((action === 'create' || action === 'create_and_view') && newItem) {
        upsertResource(newItem);
        openResourceTab({ ...newItem, type: 'bio_log' });
    }
  };

  return (
    <>
        <Stack
            direction="row"
            spacing={2}
            useFlexGap
            flexWrap="wrap"
            sx={{
            width: '100%',
            maxWidth: '1000px',
            margin: '0 auto',
            justifyContent: 'center'
            }}
        >
            <OptionCard
                title="Start Session"
                subtitle="Track a session now."
                icon={SportsGymnasticsIcon}
                color={theme.palette.secondary.main}
                onClick={handleLogClick}
            />
            <OptionCard
                title="Design Workout"
                subtitle="Create a plan."
                icon={EditIcon}
                color={theme.palette.primary.main}
                onClick={handleManualPlanClick}
            />
            {/* <OptionCard
                title="Add Bio Log"
                subtitle="Track weight, sleep, & more."
                icon={MonitorWeightIcon}
                color={theme.palette.info.main}
                onClick={handleAddBioLogClick}
            /> */}
        </Stack>

        {isPlanModalOpen && (
            <DomainObjectModal
                open={isPlanModalOpen}
                onClose={() => setIsPlanModalOpen(false)}
                onActionSuccess={handleCreateSuccess}
                itemToEdit={null}
                initialMode="edit"
                config={workoutPlanConfig}
                sectionTitle="Create Workout"
            />
        )}

        {isBioLogModalOpen && (
            <DomainObjectModal
                open={isBioLogModalOpen}
                onClose={() => setIsBioLogModalOpen(false)}
                onActionSuccess={handleBioLogCreateSuccess}
                itemToEdit={null}
                config={bioLogConfig}
                sectionTitle="Add New Bio Log"
            />
        )}
    </>
  );
};

export default CoachWelcomeOptions;